import type { SlashCommandItem } from './SlashCommandMenu';

/**
 * Bridge between TipTap's imperative Suggestion plugin and a React host rendered
 * inside the editor's DOM subtree. Rendering the menu in-tree (rather than via a
 * detached `document.body` root) keeps it a descendant of any scrollable Dialog,
 * so `react-remove-scroll` permits scrolling the menu instead of the dialog.
 */

export interface SlashMenuOpenState {
  open: true;
  query: string;
  items: SlashCommandItem[];
  /** Live caret viewport rect, in client coordinates. */
  getRect: () => DOMRect | null;
  onSelect: (item: SlashCommandItem) => void;
  onClose: () => void;
}

export interface SlashMenuClosedState {
  open: false;
}

export type SlashMenuState = SlashMenuOpenState | SlashMenuClosedState;

export interface SlashMenuStore {
  getSnapshot: () => SlashMenuState;
  subscribe: (listener: () => void) => () => void;
  set: (state: SlashMenuState) => void;
}

const CLOSED: SlashMenuClosedState = { open: false };

export function createSlashMenuStore(): SlashMenuStore {
  let state: SlashMenuState = CLOSED;
  const listeners = new Set<() => void>();

  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set: (next) => {
      // Reuse the singleton closed state so useSyncExternalStore sees a stable
      // reference and doesn't loop while the menu is idle.
      state = next.open ? next : CLOSED;
      listeners.forEach((listener) => listener());
    },
  };
}
